import moment from 'moment-timezone'
import {
  User,
  isUserPermissionSet,
  DraftUser,
  UserPermission,
  isUserPermission,
} from '../models/client/user'
import { DbUser } from '../models/database/user'
import { ApiError } from '../util/errors/ApiError'
import { CompanyMember } from '../models/client/company'
import { DbAlertEvent, DbAlertTrigger } from '../models/database/alerts'
import { AlertEvent, AlertTrigger, AlertRecipient } from '../models/client/alerts'
import { AlertTypeKey, AlertMethod, isAssetType, AssetType } from '../models/client/constants'
import { ValidationError } from '../util/errors/ValidationError'
import { printError } from '../util/print'
import {
  ObservationChangeLog,
  GateObservation,
  ObservationDraft,
  ObservationMetadata,
  DamageType,
} from '../models/client/observation'
import {
  ApiObservationAudit,
  ApiUser,
  ApiGate,
  ApiSiteWGates,
  ApiObservationResponseItem,
  ApiVehicle,
  ApiLoad,
  ApiChassis,
  ApiUserDraft,
  ApiPermission,
  ApiCheckin,
} from '../models/api'
import { isValidDateObject } from '../util/datetime'
import { Site, Gate, Geolocation } from '../models/client/site'
import { CheckIn, CheckinStatus, BoardCheckinData, CardContent } from '../models/client/checkins'
import { removeSpaces } from '../util/string'
import { BaseError } from '../util/errors/BaseError'
import { ErrorGroup } from '../util/errors/ErrorGroup'
import { AddAssetForm } from '../pages/yard-inventory/types'
import { GateFeedFilter } from './type'

export const timeoutRequest = <T>(data: T, timeMs = 90): Promise<T> =>
  new Promise((res) => {
    setTimeout(() => {
      res(data)
    }, timeMs)
  })

export const translateDbUser = (dbUser: DbUser, token: string): User => {
  if (!isUserPermissionSet(dbUser.permissions))
    throw new ApiError({
      type: 'InternalServerError',
      cause: new Error(`Invalid permission set ${JSON.stringify(dbUser.permissions)}`),
    })
  return {
    id: dbUser.id,
    companyId: dbUser.companyId,
    name: dbUser.name,
    permissions: dbUser.permissions,
    isAdmin: Boolean(dbUser.isSuperAdmin),
    username: dbUser.username || ' ',
    email: dbUser.email,
    phone: dbUser.phone || undefined,
    token,
  }
}

export const translateUser = (params: { user: ApiUser; token: string }): User => {
  const { user, token } = params
  const { id, name, permissions = [], companyId, isSuperAdmin, username, email, phone, meta } = user

  if (!id || !name || !companyId || !email) {
    throw new ValidationError({
      variableName: 'user',
      propertyName: 'user',
      expectedPropertyValue: 'User',
      propertyValue: JSON.stringify(user),
      notes: 'Missing one of the following: id || name || companyId || email',
    })
  }
  const validPermissions: UserPermission[] = []

  permissions.forEach((permission) => {
    if (!isUserPermission(permission)) {
      // const err = new ValidationError({
      //     variableName: 'user',
      //     propertyName: 'permissions',
      //     expectedPropertyValue: '"alerts" | "admin" | "audit"',
      //     propertyValue: permission,
      // })
      // eslint-disable-next-line no-console
      // console.error('Error parsing permissions', JSON.stringify(err))
      return
    }
    validPermissions.push(permission)
  })

  return {
    id,
    name,
    companyId,
    email,
    phone: phone || undefined,
    username: username || undefined,
    isAdmin: Boolean(isSuperAdmin),
    token,
    permissions: validPermissions,
    meta,
  }
}

export const translateMember = (params: { companyId: string; user: ApiUser }): CompanyMember => {
  const { companyId, user: apiUser } = params
  const user = translateUser({
    token: 'TRASH',
    user: { ...apiUser, companyId },
  })
  const { token, ...membership } = user
  return {
    ...membership,
  }
}

export const translateAlertEvent = (data: DbAlertEvent & { type: AlertTypeKey }): AlertEvent => ({
  id: data.id,
  createdAt: data.createdAt,
  triggerId: data.alertTriggerId,
  subject: data.subject,
  body: data.body,
  attachments: undefined,
  type: data.type,
})

const translateTriggerMetadata = (
  triggerType: AlertTypeKey,
  data: Record<string, unknown>
): AlertTrigger['perameters'] => {
  const getAssetType = (unknown: unknown): AssetType => {
    if (!isAssetType(unknown))
      throw new ValidationError({
        variableName: 'translateTriggerMetadata',
        propertyName: 'params.data.assetType',
        expectedPropertyValue: 'string',
        propertyValue: typeof data.assetType,
      })

    return unknown
  }

  const getAssetTypeArr = (unknown: unknown): AssetType[] => {
    if (!Array.isArray(unknown))
      throw new ValidationError({
        variableName: 'translateTriggerMetadata',
        propertyName: 'params.data.assetTypes',
        expectedPropertyValue: 'Array',
        propertyValue: typeof data.assetTypes,
      })
    const assetTypes: AssetType[] = []
    unknown.forEach((unknownItem) => {
      try {
        return assetTypes.push(getAssetType(unknownItem))
      } catch (e) {
        if (e instanceof ValidationError) {
          return printError(e.message)
        }
        throw e
      }
    })
    return assetTypes
  }

  switch (triggerType) {
    case 'assetArrivalId': {
      if (typeof data.assetId !== 'string') {
        throw new ValidationError({
          variableName: 'translateTriggerMetadata',
          propertyName: 'params.data.assetId',
          expectedPropertyValue: 'string',
          propertyValue: typeof data.assetId,
        })
      }
      return {
        type: triggerType,
        data: {
          assetId: data.assetId,
          assetType: getAssetType(data.assetType),
        },
      }
    }
    case 'assetArrivalTime': {
      if (typeof data.arrivalTimeFrom !== 'string' || typeof data.arrivalTimeTo !== 'string')
        throw new ValidationError({
          variableName: 'translateTriggerMetadata',
          propertyName: 'params.data.arrivalTimeFrom OR params.data.arrivalTimeTo',
          expectedPropertyValue: 'string',
          propertyValue: `from ${typeof data.arrivalTimeFrom}, to ${typeof data.arrivalTimeTo}`,
        })
      return {
        type: triggerType,
        data: {
          assetTypes: getAssetTypeArr(data.assetTypes),
          arrivalTimeFrom: data.arrivalTimeFrom,
          arrivalTimeTo: data.arrivalTimeTo,
        },
      }
    }
    case 'assetDepartureTime': {
      if (typeof data.departureTimeFrom !== 'string' || typeof data.departureTimeTo !== 'string')
        throw new ValidationError({
          variableName: 'translateTriggerMetadata',
          propertyName: 'params.data.departureTimeFrom OR params.data.departureTimeTo',
          expectedPropertyValue: 'string',
          propertyValue: `from ${typeof data.departureTimeFrom}, to ${typeof data.departureTimeTo}`,
        })
      return {
        type: triggerType,
        data: {
          assetTypes: getAssetTypeArr(data.assetTypes),
          departureTimeFrom: data.departureTimeFrom,
          departureTimeTo: data.departureTimeTo,
        },
      }
    }
    case 'assetMaxDwellTime': {
      if (typeof data.maxDwellTime !== 'number')
        throw new ValidationError({
          variableName: 'translateTriggerMetadata',
          propertyName: 'params.data.maxDwellTime',
          expectedPropertyValue: 'number',
          propertyValue: typeof data.maxDwellTime,
        })
      return {
        type: triggerType,
        data: {
          assetTypes: getAssetTypeArr(data.assetTypes),
          maxDwellTime: data.maxDwellTime,
        },
      }
    }
    default:
      throw new ValidationError({
        variableName: 'translateTriggerMetadata',
        propertyName: 'params.triggerType',
        expectedPropertyValue: triggerType,
        propertyValue: typeof data.assetType,
      })
  }
}

const translateAlertTriggerRecipient = (
  dbRecipient: DbAlertTrigger['alertTriggerRecipients'][number]
): AlertRecipient => {
  const methods: AlertMethod[] = []
  if (dbRecipient.smsEnabled) methods.push('sms')

  if (dbRecipient.emailEnabled) methods.push('email')

  return {
    id: dbRecipient.userId,
    methods,
    userId: dbRecipient.userId,
    createdAt: dbRecipient.createdAt,
  }
}

export const translateAlertTrigger = (
  trigger: DbAlertTrigger,
  type: AlertTypeKey
): AlertTrigger => {
  return {
    id: trigger.id,
    type,
    lastTriggeredAt: trigger.lastTriggeredAt,
    companyId: trigger.companyId,
    updatedAt: trigger.updatedAt,
    createdAt: trigger.createdAt,
    recipients: trigger.alertTriggerRecipients.map(translateAlertTriggerRecipient),
    enabled: trigger.enabled,
    perameters: translateTriggerMetadata(type, trigger.params),
  }
}

const parseToIsoDate = (variableName: string, propertyName: string, value: unknown): string => {
  if (typeof value === 'string') return value
  if (isValidDateObject(value)) return value.toISOString()

  // NOTE: silently generating error for console
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  throw new ValidationError({
    variableName,
    propertyName,
    propertyValue: typeof value,
    expectedPropertyValue: 'isoDate',
  })
}

export const translateObservationChangeLog = (data: ApiObservationAudit): ObservationChangeLog => {
  const isoCreatedAt = parseToIsoDate('ApiObservationAudit', 'createdAt', data.createdAt)
  const previousData: Record<string, any> = {}
  const nextData: Record<string, any> = {}
  const changedKeys: string[] = []
  Object.entries(data.changes || {}).forEach(([key, value]) => {
    if (!Array.isArray(value)) {
      throw new ApiError({
        type: 'InternalServerError',
        cause: new BaseError({ message: 'Invalid change' }),
      })
    }
    /* eslint-disable @typescript-eslint/no-unsafe-assignment */
    const [previous, next] = value
    previousData[key] = previous
    nextData[key] = next
    changedKeys.push(key)
    /* eslint-enable @typescript-eslint/no-unsafe-assignment */
  })
  return {
    createdAt: isoCreatedAt,
    userId: data.userId,
    action: data.action,
    comment: data.comment,
    version: data.version,
    observationid: data.observationId,
    changes: data.changes || {},
  }
}

const translateGates = (gate: ApiGate): Gate => {
  const { id, siteId, createdAt, updatedAt, tags = [], name, geolocation = {} } = gate
  if (!id) {
    throw new ValidationError({
      variableName: 'gate',
      propertyName: 'id',
      expectedPropertyValue: 'string',
      propertyValue: typeof id,
    })
  }

  let parsedGeolocation: Geolocation | undefined

  if (geolocation) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    // @ts-ignore
    const { x: lat, y: long } = geolocation
    if (!lat || !long || typeof lat !== 'number' || typeof long !== 'number')
      throw new ValidationError({
        variableName: 'gates',
        propertyName: 'lat/lng',
        expectedPropertyValue: 'number',
        propertyValue: 'undefined or null',
      })
    parsedGeolocation = {
      lat,
      long,
    }
  }
  return {
    id,
    siteId,
    createdAt,
    updatedAt,
    tags,
    name,
    geolocation: parsedGeolocation,
  }
}

export const translateSite = (data: ApiSiteWGates): Site => {
  const {
    id,
    name,
    companyId,
    timezone,
    alias,
    createdAt,
    updatedAt,
    gates,
    meta,
    locations,
    settings,
  } = data
  if (!id || !name) {
    throw new ValidationError({
      variableName: 'site',
      propertyName: 'id',
      propertyValue: typeof id,
      expectedPropertyValue: 'string',
    })
  }
  const gateDirectory: Record<string, Gate> = {}
  gates.forEach((gate) => {
    const parsedGate = translateGates(gate)
    gateDirectory[parsedGate.id] = parsedGate
  })
  return {
    id,
    name,
    companyId,
    timezone,
    alias,
    createdAt: createdAt || undefined,
    updatedAt: updatedAt || undefined,
    gates: gateDirectory,
    locations,
    meta,
    settings,
  }
}

/**
 * Makes sure no id we send to the server contains invalid characters or spaces
 * @param value
 * @returns
 */
const sanitizeID = (value: string): string => removeSpaces(value)

const codifyDamage = (damage: DamageType): string => {
  switch (damage) {
    case 'Severe':
      return '2'
    case 'Light':
      return '1'
    default:
      return '0'
  }
}

/**
 * Uses an observation draft to build an ApiObservationResponseItem that can be
 * used in creating/editing observations on the server
 * @param params.draft an observation draft object
 * @param params.isNew boolean flag that indicates if we're creating a new record
 * @param params.meta extra identifying data used to help with edit calls
 * @returns
 */
export const buildApiResponseObservation = (params: {
  draft: ObservationDraft
  isNew?: boolean
  meta?: ObservationMetadata
}): ApiObservationResponseItem => {
  const { draft, isNew, meta } = params
  const nextObservation: ApiObservationResponseItem = {
    detectedAt: draft.assetTimestamp,
    direction: draft.assetDirection ? draft.assetDirection === 'ingate' : undefined,
    hasSeal: typeof draft.hasSeal === 'boolean' ? draft.hasSeal : undefined,
    sealNumber: draft.sealNumber,
    loadDamage: codifyDamage(draft.loadDamage || 'None'),
    chassisDamage: codifyDamage(draft.chassisDamage || 'None'),
    loadRemarks: draft.loadRemarks,
    chassisRemarks: draft.chassisRemarks,
    vehicleDamage: codifyDamage(draft.vehicleDamage || 'None'),
    vehicleRemarks: draft.vehicleRemarks,
  }

  if (isNew) nextObservation.createdAt = draft.assetTimestamp

  if (draft.gateId) nextObservation.gateId = draft.gateId

  if (meta?.gateId) nextObservation.gateId = meta.gateId
  if (meta?.loadId) nextObservation.loadId = meta.loadId
  if (meta?.vehicleId) nextObservation.vehicleId = meta.vehicleId
  if (meta?.chassisId) nextObservation.chassisId = meta.chassisId

  const draftVehicle: Partial<ApiVehicle> = {}
  if (draft.vehicleNumber) draftVehicle.number = sanitizeID(draft.vehicleNumber)
  if (meta?.vehicleId) draftVehicle.id = meta.vehicleId
  if (draft.vehicleCarrierId) draftVehicle.carrierId = draft.vehicleCarrierId
  // if (draft.vehicleColorId) draftVehicle.colorId = draft.vehicleColorId
  if (draft.vehicleState) draftVehicle.state = draft.vehicleState
  if (draft.vehicleTypeId) draftVehicle.typeId = draft.vehicleTypeId
  if (draft.vehicleLicensePlate) draftVehicle.licensePlate = sanitizeID(draft.vehicleLicensePlate)
  if (draft.vehicleDotNumber) draftVehicle.dotNumber = draft.vehicleDotNumber
  if (draft.vehicleWeightClass) draftVehicle.weightClass = draft.vehicleWeightClass
  if (draft.vehicleFuelType) draftVehicle.fuelType = draft.vehicleFuelType
  if (Object.keys(draftVehicle).length > 0) nextObservation.vehicle = draftVehicle

  const draftLoad: Partial<ApiLoad> = {}
  if (meta?.loadId) draftLoad.id = meta.loadId
  if (draft.loadNumber) draftLoad.number = sanitizeID(draft.loadNumber)
  if (draft.loadCustomerName) draftLoad.bco = draft.loadCustomerName
  if (draft.loadTypeId) draftLoad.typeId = draft.loadTypeId
  if (meta?.loadId) draftLoad.id = meta.loadId
  if (draft.loadState) draftLoad.state = draft.loadState
  if (draft.loadLicensePlate) draftLoad.licensePlate = draft.loadLicensePlate
  // if (draft.loadColorId) draftLoad.colorId = draft.loadColorId
  if (draft.assetTypeId) draftLoad.typeId = draft.assetTypeId
  if (Object.keys(draftLoad).length > 0) nextObservation.load = draftLoad

  const draftChassis: Partial<ApiChassis> = {}

  if (draft.chassisNumber) draftChassis.number = sanitizeID(draft.chassisNumber)
  if (draft.chassisNumber) draftChassis.typeId = 'Default'
  if (draft?.chassisTypeId) draftChassis.typeId = draft.chassisTypeId
  if (meta?.chassisId) draftChassis.id = meta.chassisId
  if (draft.chassisLicensePlate) draftChassis.licensePlate = draft.chassisLicensePlate
  if (draft.chassisState) draftChassis.state = draft.chassisState
  // if (draft.chassisColorId) draftChassis.colorId = draft.chassisColorId
  if (Object.keys(draftChassis).length > 0) nextObservation.chassis = draftChassis

  // if (draft.vehicleDwellTimeHr) {
  //     nextObservation.vehicleOutwardObservation = {
  //         dwellTime: draft.vehicleDwellTimeHr,
  //     }
  //     if (meta?.vehicleOutwardObservationId) {
  //         nextObservation.vehicleOutwardObservation = {
  //             id: meta.vehicleOutwardObservationId,
  //         }
  //     }
  // }
  // if (draft.loadDwellTimeHr) {
  //     nextObservation.loadOutwardObservation = {
  //         dwellTime: draft.loadDwellTimeHr,
  //     }
  //     if (meta?.loadOutwardObservationId)
  //         nextObservation.loadOutwardObservation.id = meta.loadOutwardObservationId
  // }
  //
  // if (draft.chassisDwellTimeHr) {
  //     nextObservation.chassisOutwardObservation = {
  //         dwellTime: draft.chassisDwellTimeHr,
  //     }
  //     if (meta?.chassisOutwardObservationId)
  //         nextObservation.chassisOutwardObservation.id = meta.loadOutwardObservationId
  // }

  return nextObservation
}

/**
 * Uses an observation draft to build an ApiObservationResponseItem that can be
 * used in creating/editing observations on the server
 * @param params.draft an observation draft object
 * @param params.isNew boolean flag that indicates if we're creating a new record
 * @param params.meta extra identifying data used to help with edit calls
 * @returns
 */
export const buildApiResponseAsset = (params: {
  draft: AddAssetForm
  isNew?: boolean
  meta?: ObservationMetadata
}): ApiObservationResponseItem => {
  const { draft, isNew, meta } = params

  const nextObservation: ApiObservationResponseItem = {
    detectedAt: getTimestamp(draft.ingateDate, draft.ingateTime),
    direction: draft.assetStatus === 'in-the-yard' ? true : false,
    hasSeal: typeof draft.sealPresent === 'boolean' ? draft.sealPresent : undefined,
    sealNumber: draft.sealNumber,
    loadDamage: '0',
    chassisDamage: '0',
    loadRemarks: draft.loadRemarks,
    chassisRemarks: draft.chassisRemarks,
    vehicleDamage: '0',
    vehicleRemarks: draft.vehicleRemarks,
  }

  if (isNew) nextObservation.createdAt = getTimestamp(draft.ingateDate, draft.ingateTime)

  if (draft.gate) nextObservation.gateId = draft.gate.value

  const draftVehicle: Partial<ApiVehicle> = {}
  if (draft.truckNumber) draftVehicle.number = sanitizeID(draft.truckNumber)
  if (draft.carrier) draftVehicle.carrierId = draft.carrier.value
  if (draft.vehicleType) draftVehicle.typeId = draft.vehicleType.value
  if (draft.truckLPNumber) draftVehicle.licensePlate = sanitizeID(draft.truckLPNumber)
  if (draft.truckDOT) draftVehicle.dotNumber = draft.truckDOT
  if (draft.truckClass) draftVehicle.weightClass = draft.truckClass.value
  if (draft.fuelType) draftVehicle.fuelType = draft.fuelType.value

  if (Object.keys(draftVehicle).length > 0) nextObservation.vehicle = draftVehicle

  const draftLoad: Partial<ApiLoad> = {}
  if (draft.loadNumber) draftLoad.number = sanitizeID(draft.loadNumber)
  if (draft.bco) draftLoad.bco = draft.bco
  if (draft.loadType) draftLoad.typeId = draft.loadType.value
  if (draft.loadLPNumber) draftLoad.licensePlate = draft.loadLPNumber
  if (Object.keys(draftLoad).length > 0) nextObservation.load = draftLoad

  const draftChassis: Partial<ApiChassis> = {}
  if (draft.chassisNumber) draftChassis.number = sanitizeID(draft.chassisNumber)
  if (draft.chassisNumber) draftChassis.typeId = 'Default'
  if (draft.chassisLPNumber) draftChassis.licensePlate = draft.chassisLPNumber
  if (Object.keys(draftChassis).length > 0) nextObservation.chassis = draftChassis

  nextObservation.vehicleId = meta?.vehicleId ? meta.vehicleId : null
  nextObservation.chassisId = meta?.chassisId ? meta.chassisId : null
  nextObservation.loadId = meta?.loadId ? meta.loadId : null

  return nextObservation
}

const getTimestamp = (date: string, time: string): string => {
  const newDate = new Date(date)
  newDate.setHours(Number(time.split(':')[0]))
  newDate.setMinutes(Number(time.split(':')[1]))

  return newDate.toISOString()
}

const translateDamage = (damageCode: string | null | undefined): DamageType => {
  switch (damageCode) {
    case '1':
      return 'Light'
    case '2':
      return 'Severe'
    default:
      return 'None'
  }
}

export const getFormattedDate = (str: string): string => {
  const utcTime = new Date(str)
  const localTime = moment(moment.utc(utcTime).toDate()).format('MM/DD/YYYY hh:mm:ss A')

  return localTime.toString()
}

export const translateObservation = (
  apiObservation: ApiObservationResponseItem
): GateObservation => {
  if (!apiObservation.id)
    throw new ValidationError({
      variableName: 'ApiObservationResponseItem',
      propertyName: 'id',
      expectedPropertyValue: 'string',
      propertyValue: JSON.stringify(apiObservation.id),
    })

  // FIXME: map type IDs: asset, vehicle, trailer
  // right now we're staticlly inserting "asset types"
  return {
    siteId: apiObservation?.gate?.siteId,
    gateId: apiObservation?.gateId,
    observationId: apiObservation.id,
    timezone: apiObservation.gate?.site?.timezone,
    assetTimestamp: apiObservation.detectedAt,
    assetType: apiObservation.load?.type?.name,
    assetTypeId: apiObservation.load?.typeId,
    assetDirection: apiObservation.direction ? 'ingate' : 'outgate',
    assetPreviewUrl: apiObservation.imagePath,
    assetImageUrls: apiObservation?.imageNames || [],
    assetImagePaths: apiObservation?.imagePaths || [],
    vehicleId: apiObservation.vehicle?.id,
    vehicleLicensePlate: apiObservation.vehicle?.licensePlate,
    vehicleNumber: apiObservation.vehicle?.number,
    vehicleDotNumber: apiObservation.vehicle?.dotNumber,
    // vehicleState: apiObservation.vehicle?.state,
    vehicleType: apiObservation.vehicle?.type?.name,
    vehicleTypeId: apiObservation.vehicle?.typeId,
    vehicleCarrierId: apiObservation.vehicle?.carrierId,
    vehicleCarrierName: apiObservation?.vehicle?.carrier?.name,
    // vehicleColor: apiObservation?.vehicle?.color?.name,
    vehicleDamage: translateDamage(apiObservation.vehicleDamage),
    vehicleRemarks: apiObservation.vehicleRemarks,
    vehicleWeightClass: apiObservation.vehicle?.weightClass,
    vehicleFuelType: apiObservation.vehicle?.fuelType,
    vehicleIsPresent: !apiObservation.vehicleOutwardObservationId,
    vehicleDwellTimeHr: apiObservation?.vehicleDwellTime,
    hasSeal: apiObservation.hasSeal,
    sealNumber: apiObservation.sealNumber,
    loadDwellTimeHr: apiObservation.loadDwellTime,
    loadCustomerName: apiObservation.load?.bco || null,
    loadNumber: apiObservation.load?.number,
    // loadColor: apiObservation.load?.color?.name,
    loadType: apiObservation.load?.type?.name,
    loadTypeId: apiObservation.load?.typeId,
    loadId: apiObservation.loadId,
    loadOutwardObservationId: apiObservation.loadOutwardObservationId,
    loadOutwardObservation: apiObservation.loadOutwardObservation || null,
    loadLicensePlate: apiObservation.load?.licensePlate,
    // loadState: apiObservation.load?.state,
    loadIsPresent: !apiObservation.loadOutwardObservationId,
    loadRemarks: apiObservation.loadRemarks,
    chassisNumber: apiObservation?.chassis?.number,
    chassisId: apiObservation.chassisId,
    chassisLicensePlate: apiObservation?.chassis?.licensePlate,
    // chassisState: apiObservation.chassis?.state,
    // chassisColor: apiObservation.chassis?.color?.name,
    chassisIsPresent: !apiObservation.chassisOutwardObservationId,
    chassisOutwardObservationId: apiObservation?.chassisOutwardObservationId,
    chassisOutwardObservation: apiObservation.chassisOutwardObservation || null,
    loadDamage: translateDamage(apiObservation?.loadDamage),
    chassisDwellTimeHr: apiObservation?.chassisDwellTime,
    chassisDamage: translateDamage(apiObservation.chassisDamage),
    chassisRemarks: apiObservation.chassisRemarks,
    vehicleOutwardObservationId: apiObservation?.vehicleOutwardObservationId,
    vehicleOutwardObservation: apiObservation?.vehicleOutwardObservation || null,
    isBare: apiObservation?.chassisIsBare,
    meta: apiObservation.meta,
    tags: apiObservation.tags,
    // FIXME: how do I find theses data points
    // loadOwner: unknown
  }
}

export const translateObservationV2 = (
  apiObservation: ApiObservationResponseItem
): GateObservation => {
  if (!apiObservation.id) {
    throw new ValidationError({
      variableName: 'ApiObservationResponseItem',
      propertyName: 'id',
      expectedPropertyValue: 'string',
      propertyValue: JSON.stringify(apiObservation.id),
    })
  }

  return {
    siteId: apiObservation?.gate?.siteId,
    gateId: apiObservation?.gateId,
    observationId: apiObservation.id,
    timezone: apiObservation.timezone,
    assetTimestamp: apiObservation.detectedAt,
    assetType: apiObservation?.loadType,
    assetTypeId: apiObservation.load?.typeId,
    assetDirection: apiObservation.direction ? 'ingate' : 'outgate',
    assetPreviewUrl: apiObservation.imagePath,
    assetImageUrls: apiObservation?.imageNames || [],
    assetImagePaths: apiObservation?.imagePaths || [],
    vehicleId: apiObservation?.vehicleId,
    vehicleLicensePlate: apiObservation?.vehicleLicensePlate,
    vehicleNumber: apiObservation?.vehicleNumber,
    vehicleDotNumber: apiObservation?.dotNumber,
    vehicleType: apiObservation.vehicleTypeName,
    vehicleCarrierName: apiObservation?.carrierName,
    vehicleDamage: translateDamage(apiObservation.vehicleDamage),
    vehicleRemarks: apiObservation.vehicleRemarks,
    vehicleWeightClass: apiObservation?.vehicleWeightClass,
    vehicleFuelType: apiObservation?.vehicleFuelType,
    vehicleIsPresent: !apiObservation.vehicleOutwardObservationId,
    vehicleDwellTimeHr: apiObservation?.vehicleDwellTime,
    hasSeal: apiObservation.hasSeal,
    sealNumber: apiObservation.sealNumber,
    loadDwellTimeHr: apiObservation.loadDwellTime,
    loadCustomerName: apiObservation?.bco || null,
    loadNumber: apiObservation?.loadNumber,
    loadType: apiObservation.loadType,
    loadId: apiObservation.loadId,
    loadOutwardObservationId: apiObservation.loadOutwardObservationId,
    loadOutwardObservation: apiObservation.loadOutwardObservation || null,
    loadLicensePlate: apiObservation.loadLicensePlate,
    loadIsPresent: !apiObservation.loadOutwardObservationId,
    loadRemarks: apiObservation.loadRemarks,
    chassisNumber: apiObservation?.chassisNumber,
    chassisId: apiObservation.chassisId,
    chassisLicensePlate: apiObservation?.chassisLicensePlate,
    chassisIsPresent: !apiObservation.chassisOutwardObservationId,
    chassisOutwardObservationId: apiObservation?.chassisOutwardObservationId,
    chassisOutwardObservation: apiObservation.chassisOutwardObservation || null,
    loadDamage: translateDamage(apiObservation?.loadDamage),
    chassisDwellTimeHr: apiObservation?.chassisDwellTime,
    chassisDamage: translateDamage(apiObservation.chassisDamage),
    chassisRemarks: apiObservation.chassisRemarks,
    vehicleOutwardObservationId: apiObservation?.vehicleOutwardObservationId,
    vehicleOutwardObservation: apiObservation?.vehicleOutwardObservation || null,
    isBare: apiObservation?.chassisIsBare,
    meta: apiObservation.meta,
    tags: apiObservation.tags,
  }
}

export const buildApiUser = (user: DraftUser, meta?: { isNew?: boolean }): ApiUserDraft => {
  const errors: BaseError[] = []

  if (!user?.companyId) {
    errors.push(
      new ValidationError({
        variableName: 'User',
        propertyName: 'companyId',
        expectedPropertyValue: 'string',
        propertyValue: typeof user?.companyId,
        cause: new Error('Company ID required'),
      })
    )
  }

  if (!user?.email) {
    errors.push(
      new ValidationError({
        variableName: 'User',
        propertyName: 'email',
        expectedPropertyValue: 'string',
        propertyValue: typeof user?.email,
        cause: new Error('Email required'),
      })
    )
  }

  if (meta?.isNew && !user?.password) {
    errors.push(
      new ValidationError({
        variableName: 'User',
        propertyName: 'password',
        expectedPropertyValue: 'string',
        propertyValue: typeof user?.password,
        cause: new Error('Email required'),
      })
    )
  }

  const permissions = user?.permissions || []
  const apiPermissions: ApiPermission[] = []

  permissions.forEach((permission, index) => {
    switch (permission) {
      // case 'admin':
      //     apiPermissions.push(ApiPermission.admin)
      //     break
      // case 'alerts':
      //     apiPermissions.push(ApiPermission.alerts)
      //     break
      case 'audits':
        apiPermissions.push(ApiPermission.audits)
        break
      case 'users':
        apiPermissions.push(ApiPermission.users)
        break
      case 'checkins':
        apiPermissions.push(ApiPermission.checkins)
        break
      default:
      // errors.push(
      //     new ValidationError({
      //         variableName: 'user',
      //         propertyName: `permissions[${index}]`,
      //         expectedPropertyValue: 'users | audit',
      //         propertyValue: permission
      //     })
      // )
    }
  })

  if (errors.length > 0) {
    throw new ErrorGroup(errors)
  }

  const nextUser: ApiUserDraft = {
    name: user?.name,
    username: user?.username,
    email: user?.email,
    password: user?.password,
    phone: user?.phone,
    permissions: apiPermissions,
  }

  if (meta?.isNew) {
    nextUser.companyId = user?.companyId
  }

  if (!meta?.isNew) {
    nextUser.id = user?.id
  }

  return nextUser
}

const compareDate = (date1, date2) => {
  const dt1 = new Date(date1)
  const dt2 = new Date(date2)

  const res = dt2.getTime() - dt1.getTime()

  return res
}

export const translateCheckinToBoardData = (
  checkinStatus: CheckinStatus[],
  data: CheckIn[],
  yardType: string
): BoardCheckinData => {
  const result = {
    lanes: [],
  }

  checkinStatus?.forEach((checkinStatus: CheckinStatus) => {
    let laneName = checkinStatus.name
    if (yardType === 'yard' && checkinStatus.key === 'docking') {
      laneName = 'Parking'
    }
    if (yardType === 'yard' && checkinStatus.key === 'outgated') {
      laneName = 'Completed'
    }
    // if site employee type
    if (yardType === 'employee') {
      if (checkinStatus.key === 'ingated') {
        laneName = 'Check-In'
      }
      if (checkinStatus.key === 'outgated') {
        laneName = 'Checkout'
      }
      if (checkinStatus.key === 'docking') {
        return true
      }
    }
    if (yardType === 'checkout_only') {
      if (checkinStatus.key === 'ingated') {
        laneName = 'Queue (Pending)'
      }
      if (checkinStatus.key === 'outgated') {
        laneName = 'Checked-In'
      }
      if (checkinStatus.key === 'docking') {
        return true
      }
    }

    result.lanes.push({
      id: checkinStatus.id,
      title: laneName,
      label: '',
      style: {
        width: '400px',
        backgroundColor: '#f4f7fa',
      },
      cardStyle: {
        maxWidth: '360px',
        width: '360px',
        fontSize: '16px',
      },
      key: checkinStatus.key,
      cards: [],
    })
  })

  const statusMap = result.lanes.map(
    (val: { id: string; title: string; label: string; cards: CardContent[] }) => {
      return val.id
    }
  )

  data?.forEach((checkin: CheckIn) => {
    const {
      id,
      firstName,
      lastName,
      phoneNumber,
      vehicleLicensePlate,
      loadNumber,
      assetNumber,
      chassisNumber,
      carrierName,
      customerName,
      securitySeal,
      outgatedAt,
      companyId,
      siteId,
      dockId,
      dock,
      fuelTypeId,
      observationId,
      statusId,
      meta,
      tags,
      createdAt,
      updatedAt,
      status,
      isDropOff,
      conversation,
      appointmentAt,
      viewedAt,
    } = checkin

    if (!id) {
      throw new ValidationError({
        variableName: 'checkin',
        propertyName: 'id',
        propertyValue: typeof statusId,
        expectedPropertyValue: 'valid id',
      })
    }
    // Invalid Status
    if (!statusId) {
      console.error('Invalid Status Assigned', checkin)
      throw new ValidationError({
        variableName: 'checkin',
        propertyName: 'statusId',
        propertyValue: typeof id,
        expectedPropertyValue: 'string',
      })
    } else if (statusMap.includes(statusId)) {
      let cardTags = []
      if (isDropOff !== null) {
        if (!isDropOff) {
          cardTags.push({
            bgcolor: '#0079BF',
            color: 'white',
            title: ' PICK UP ',
          })
        } else {
          cardTags.push({
            bgcolor: '#A3DA8D',
            color: 'white',
            title: ' DROP OFF ',
          })
        }
      } else if (tags && tags.length) {
        const tagColors = {
          dropoff: '#A3DA8D',
          pickup: '#0079BF',
          ltl: '#FFAD60',
        }
        tags.forEach((t) => {
          cardTags.push({
            bgcolor: tagColors[t] || '#0079BF',
            color: 'white',
            title: ` ${t.toUpperCase()} `,
          })
        })
      }
      let cardColor = '#FFFFFF'
      if (
        result.lanes[statusMap.indexOf(statusId)].key === 'ingated' ||
        result.lanes[statusMap.indexOf(statusId)].key === 'docking'
      ) {
        const checkinTime = moment.utc(createdAt)
        const timeDiff = moment().utc().diff(checkinTime)
        const minutesDiff = moment().utc().diff(checkinTime, 'minutes')
        let timeTitle = ` ${minutesDiff} MINUTES`
        if (minutesDiff >= 90 && minutesDiff < 120) {
          cardColor = '#FFAD60'
        } else if (minutesDiff >= 120) {
          cardColor = '#FF6363'
          const diffDuration = moment.duration(timeDiff)
          timeTitle = `${diffDuration.hours()} HOURS ${diffDuration.minutes()} MINUTES`
          if (minutesDiff > 1440) {
            timeTitle = `${diffDuration.days()} DAYS ${timeTitle}`
          }
        }
        cardTags.push({
          bgcolor: '#FFFFFF',
          color: '#000000',
          title: timeTitle,
        })
      } else {
        cardColor = '#E8EAE6'
      }
      if (yardType === 'yard') {
        cardColor = '#FFFFFF'
      }
      const verbiage = yardType === 'yard' ? 'Zone' : 'Dock'
      // create description data
      const description = [
        `Name : ${firstName} ${lastName}\nPhone Number : ${phoneNumber || ''}\n\n`,
      ]
      if (yardType === 'employee') {
        description.push(`Employee Number : ${carrierName || ''}\n\n`)
      } else {
        description.push(
          `Booking/PO/BOL : ${loadNumber || ''}\nContainer/Trailer Number : ${
            assetNumber || ''
          }\n\n`
        )
      }

      description.push(`Created At : ${getFormattedDate(createdAt)}\n`)
      if (appointmentAt) {
        description.push(`Appointment Time : ${getFormattedDate(appointmentAt)}`)
      }
      // if there are new messages for this card
      const userMessages = conversation?.sms?.filter((s) => s.inboundMessageId)
      let isNewMessage = false
      if (userMessages && userMessages.length) {
        const latestMessage = userMessages[userMessages.length - 1]
        if (!viewedAt || latestMessage.createdAt > viewedAt) {
          isNewMessage = true
        }
      }

      let title = `${dock?.meta.name ? `${verbiage} ${dock?.meta.name}` : `Unassigned ${verbiage}`}`

      // no title for employee yards
      if (yardType === 'employee') {
        title = ''
        cardTags = []
      }
      result.lanes[statusMap.indexOf(statusId)].cards.push({
        id,
        title,
        name: `${firstName} ${lastName}`,
        firstName,
        lastName,
        description: description.join(''),
        label: `${carrierName?.toUpperCase()}`,
        style: {
          backgroundColor: cardColor,
          color: '#FFFFFF',
          maxWidth: '360px',
          width: '360px',
          fontSize: '16px',
        },
        siteId,
        draggable: true,
        vehicleLicensePlate,
        phoneNumber,
        loadNumber,
        assetNumber,
        chassisNumber,
        securitySeal,
        carrierName,
        customerName,
        fuelTypeId,
        meta,
        isDropOff,
        createdAt,
        updatedAt,
        outgatedAt,
        dockId,
        statusId,
        dockNumber: dock?.meta.name ? dock?.meta.name : -1,
        tags: cardTags,
        conversation,
        childCards: [],
        isNewMessage,
      })
    }
  })
  return {
    lanes: result.lanes.map((lane) => {
      const { cards } = lane
      const newCards = []
      if (lane.key === 'outgated' && yardType !== 'yard') {
        const cardByPhone = {}

        for (const card of cards) {
          if (!cardByPhone[card.phoneNumber]) {
            cardByPhone[card.phoneNumber] = []
          }
          cardByPhone[card.phoneNumber].push(card)
        }
        Object.keys(cardByPhone).forEach((key) => {
          const entries = cardByPhone[key]
          if (entries.length === 1) {
            newCards.push(entries[0])
          } else {
            const sortedEntries = entries.sort((a, b) => compareDate(a.createdAt, b.createdAt))
            const mainCard = sortedEntries.shift()
            mainCard.childCards = sortedEntries
            newCards.push(mainCard)
          }
        })

        lane.cards = newCards
      }
      return {
        ...lane,
        cards: lane.cards.sort((a, b) => compareDate(a.createdAt, b.createdAt)),
      }
    }),
  }
}

export function buildVehicleAssetObject(asset, isAttachment) {
  return {
    id: asset.id,
    vehicleCarrierId: asset.vehicleCarrierId,
    vehicleDamage: asset.vehicleDamage,
    vehicleDwellTime: asset.vehicleDwellTime,
    vehicleFuelType: asset.vehicleFuelType,
    vehicleId: asset.vehicleId,
    vehicleLicensePlate: asset.vehicleLicensePlate,
    vehicleNumber: asset.vehicleNumber,
    vehicleRemarks: asset.vehicleRemarks,
    vehicleDotNumber:
      asset.dotNumber || asset?.meta?.carrier?.dotNumber || asset?.vehicle?.dotNumber,
    vehicleOutwardObservationId: asset.vehicleOutwardObservationId,
    detectedAt: asset.detectedAt,
    assetName: asset.vehicleTypeName || asset?.vehicle?.type?.name,
    isAttachment,
    isOutgated: !!asset.vehicleOutwardObservationId,
  }
}

export function buildChassisAssetObject(asset, isAttachment) {
  return {
    id: asset.id,
    chassisId: asset.chassisId,
    chassisNumber: asset.chassisNumber || asset?.chassis?.number,
    chassisLicensePlate: asset.chassisLicensePlate,
    chassisDwellTime: asset.chassisDwellTime,
    chassisDamage: asset.chassisDamage,
    chassisIsBare: asset.chassisIsBare,
    chassisOutwardObservationId: asset.chassisOutwardObservationId,
    chassisRemarks: asset.chassisRemarks,
    detectedAt: asset.detectedAt,
    assetName: 'Chassis',
    isAttachment,
    isOutgated: !!asset.chassisOutwardObservationId,
  }
}

export function buildLoadAssetObject(asset, isAttachment) {
  return {
    id: asset.id,
    loadDamage: asset.loadDamage,
    loadDwellTime: asset.loadDwellTime,
    loadId: asset.loadId,
    loadLicensePlate: asset.loadLicensePlate,
    loadNumber: asset.loadNumber || asset?.load?.number,
    loadOutwardObservationId: asset.loadOutwardObservationId,
    loadRemarks: asset.loadRemarks,
    loadType: asset.loadType || asset?.load?.type?.name,
    hasSeal: asset.hasSeal,
    sealNumber: asset.sealNumber,
    detectedAt: asset.detectedAt,
    assetName: asset.loadType || asset?.load?.type?.name,
    isAttachment,
    isOutgated: !!asset.loadOutwardObservationId,
  }
}

export function isVehicleAsset(apiAsset) {
  const vehicleTypes = ['Tractor', 'Forklift', 'Service', 'GOAT', 'Other']
  return [apiAsset.vehicleTypeName, apiAsset.vehicle?.type?.name].some((type) =>
    vehicleTypes.includes(type)
  )
}

export function isLoadAsset(apiAsset) {
  const loadTypes = ['Container', 'Container Reefer', 'Dry Van', 'Dry Van Reefer', 'Flatbed']
  return [apiAsset.loadType, apiAsset?.load?.type?.name].some((type) => loadTypes.includes(type))
}

export const translateAsset = (apiAsset, isBare) => {
  if (!apiAsset?.id) {
    return
  }

  const assets: any[] = []

  const isVehicleOut = apiAsset?.vehicleOutwardObservationId
  const isChassisOut = apiAsset?.chassisOutwardObservationId
  const isLoadOut = apiAsset?.loadOutwardObservationId

  if (isVehicleOut) {
    if (apiAsset.chassisId && !isChassisOut) {
      assets.push(buildChassisAssetObject(apiAsset, false))
    }

    if (isLoadAsset(apiAsset) && !isLoadOut) {
      assets.push(buildLoadAssetObject(apiAsset, !!apiAsset.chassisId))
    }

    return assets
  }

  if (isChassisOut) {
    if (isVehicleAsset(apiAsset) && !isVehicleOut) {
      assets.push(buildVehicleAssetObject(apiAsset, false))
    }

    if (isLoadAsset(apiAsset) && !isLoadOut) {
      assets.push(buildLoadAssetObject(apiAsset, !!apiAsset.vehicleId))
    }

    return assets
  }

  if (isVehicleAsset(apiAsset) && !isBare) {
    assets.push(buildVehicleAssetObject(apiAsset, false))

    if (apiAsset.chassisId && !isChassisOut) {
      assets.push(buildChassisAssetObject(apiAsset, true))
    }

    if (isLoadAsset(apiAsset) && !isLoadOut) {
      assets.push(buildLoadAssetObject(apiAsset, true))
    }

    return assets
  }

  if (apiAsset.chassisId && !isChassisOut) {
    assets.push(buildChassisAssetObject(apiAsset, false))
  }

  if (isLoadAsset(apiAsset) && !isLoadOut) {
    assets.push(buildLoadAssetObject(apiAsset, false))
  }

  return assets
}

export const getAssetData = (apiAsset) => {
  if (!apiAsset?.id) {
    return
  }

  const assets: any[] = []

  if (apiAsset.chassisId) {
    assets.push(buildChassisAssetObject(apiAsset, false))
  }

  if (isLoadAsset(apiAsset)) {
    assets.push(buildLoadAssetObject(apiAsset, false))
  }

  return assets
}

const FILTER_TYPES = ['loadType', 'vehicleType', 'chassisType']

export function getActiveBigNumberFilter(filters: GateFeedFilter[]) {
  return filters.find((filter) => FILTER_TYPES.includes(filter.type))
}

export function applyAssetsFilters(assets: any[], filters: GateFeedFilter[]) {
  const activeBigNumberFilter = getActiveBigNumberFilter(filters)

  if (!activeBigNumberFilter) return assets

  const filteredAssets = assets.filter((asset) => {
    if (activeBigNumberFilter.type === 'loadType') {
      return asset.loadType === activeBigNumberFilter.label
    }
    if (activeBigNumberFilter.type === 'chassisType') {
      return asset.assetName === activeBigNumberFilter.label
    }
    if (activeBigNumberFilter.type === 'vehicleType') {
      return [asset.loadType, asset.assetName].includes(activeBigNumberFilter.label)
    }
  })

  return filteredAssets
}

export function translateGateFeedObservation(observation) {
  if (!observation?.id) {
    return
  }

  return {
    id: observation.id,
    vehicleCarrierId: observation.vehicleCarrierId,
    carrierName: observation.carrierName,
    vehicleDamage: observation.vehicleDamage,
    vehicleDwellTime: observation.vehicleDwellTime,
    vehicleTypeName: observation.vehicleTypeName,
    vehicleFuelType: observation.vehicleFuelType,
    vehicleId: observation.vehicleId,
    vehicleLicensePlate: observation.vehicleLicensePlate,
    vehicleNumber: observation.vehicleNumber,
    vehicleRemarks: observation.vehicleRemarks,
    vehicleDotNumber:
      observation.dotNumber ||
      observation?.meta?.carrier?.dotNumber ||
      observation?.vehicle?.dotNumber,
    chassisId: observation.chassisId,
    chassisNumber: observation.chassisNumber || observation?.chassis?.number,
    chassisLicensePlate: observation.chassisLicensePlate,
    chassisDwellTime: observation.chassisDwellTime,
    chassisDamage: observation.chassisDamage,
    chassisIsBare: observation.chassisIsBare,
    chassisOutwardObservationId: observation.chassisOutwardObservationId,
    chassisRemarks: observation.chassisRemarks,
    loadDamage: observation.loadDamage,
    loadDwellTime: observation.loadDwellTime,
    loadId: observation.loadId,
    loadLicensePlate: observation.loadLicensePlate,
    loadNumber: observation.loadNumber || observation?.load?.number,
    loadOutwardObservationId: observation.loadOutwardObservationId,
    loadRemarks: observation.loadRemarks,
    loadType: observation.loadType || observation?.load?.type?.name,
    detectedAt: observation.detectedAt,
    tags: observation.tags,
    hasSeal: observation.hasSeal,
    sealNumber: observation.sealNumber,
    assetPreviewUrl: observation.imagePath,
    assetImageUrls: observation?.imageNames || [],
    assetImagePaths: observation?.imagePaths || [],
    direction: observation.direction,
    timezone: observation.timezone,
  }
}
